// SectorTreemap (TASK-496) — ported from SixEase widgets/SectorTreemap.tsx, re-grounded and
// re-tokenised. SixEase read industry_group + current_chf + fit_score off a single getFit call;
// Kielis FitHolding has NO industry_group, so we group getPositions by industry_group (chf =
// market_value) and JOIN getFit by position_id↔Position.id for the fit colouring (degrades to
// grey when a position has no score). Row-sliced pseudo-treemap (no chart lib).
import { useQuery } from '@tanstack/react-query';
import { portfolioApiService } from '../../../services/api/portfolioApiService';
import { useFit } from '../../../hooks/useFit';
import { WidgetContainer } from '../WidgetContainer';

const SVG_W = 400;
const SVG_H = 280;

interface SectorData {
  name: string;
  chf: number;
  count: number;
  avgFit: number | null;
}

// Pure hex — portable, viz colours (no shadcn semantic token for green/amber).
function sectorFillColor(avgFit: number | null): string {
  if (avgFit === null) return '#94a3b8'; // slate — unscored
  if (avgFit === 0) return '#ef4444'; // red — conflict
  if (avgFit >= 0.75) return '#22c55e'; // green — clean
  return '#f59e0b'; // amber — partial
}

interface PositionLike {
  id: string;
  industry_group?: string;
  market_value: number;
}

function computeSectors(positions: PositionLike[], fitById: Map<string, number>): SectorData[] {
  const map = new Map<string, { chf: number; scores: number[]; count?: number }>();
  for (const p of positions) {
    const key = p.industry_group || 'Other';
    const existing = map.get(key) ?? { chf: 0, scores: [], count: 0 };
    existing.chf += Number(p.market_value) || 0;
    const fit = fitById.get(p.id);
    if (fit != null) existing.scores.push(fit);
    existing.count = (existing.count ?? 0) + 1;
    map.set(key, existing);
  }
  return Array.from(map.entries())
    .map(([name, { chf, scores, count }]) => ({
      name,
      chf,
      count: count ?? 0,
      avgFit: scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null,
    }))
    .sort((a, b) => b.chf - a.chf);
}

function TreemapCell({ x, y, w, h, sector, totalChf }: { x: number; y: number; w: number; h: number; sector: SectorData; totalChf: number }) {
  const fill = sectorFillColor(sector.avgFit);
  const chfK = sector.chf >= 1_000_000 ? `${(sector.chf / 1_000_000).toFixed(1)}M` : `${(sector.chf / 1_000).toFixed(0)}K`;
  const pct = totalChf > 0 ? ((sector.chf / totalChf) * 100).toFixed(1) : '0.0';
  const showLabel = w > 50 && h > 22;
  const showChf = w > 60 && h > 36;
  return (
    <g>
      {/* TASK-654: every cell — however small — exposes its full detail. */}
      <title>{`${sector.name} — ${pct}% · CHF ${chfK} · ${sector.count} holding${sector.count === 1 ? '' : 's'}`}</title>
      <rect
        x={x + 1}
        y={y + 1}
        width={Math.max(w - 2, 0)}
        height={Math.max(h - 2, 0)}
        fill={fill}
        fillOpacity="0.25"
        rx="4"
        stroke={fill}
        strokeOpacity="0.5"
        strokeWidth="1"
      />
      {showLabel && (
        <text
          x={x + w / 2}
          y={y + (showChf ? h / 2 - 6 : h / 2 + 4)}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="10"
          fill="hsl(var(--foreground))"
          fontWeight="500"
        >
          {sector.name.length > 14 ? sector.name.slice(0, 13) + '…' : sector.name}
        </text>
      )}
      {showChf && (
        <text x={x + w / 2} y={y + h / 2 + 8} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="hsl(var(--muted-foreground))">
          {chfK}
        </text>
      )}
    </g>
  );
}

export function SectorTreemap({ clientId }: { clientId: string }) {
  // Positions is required (gates error); fit only colours the cells and degrades to null.
  const posQuery = useQuery({
    queryKey: ['portfolio-positions', clientId],
    queryFn: () => portfolioApiService.getPositions(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
  const fitQuery = useFit(clientId);

  const loading = posQuery.isLoading || fitQuery.isLoading;
  const fitById = new Map<string, number>();
  for (const h of fitQuery.data?.holdings ?? []) {
    if (h.fit_score != null) fitById.set(h.position_id, h.fit_score);
  }
  const sectors = posQuery.data ? computeSectors(posQuery.data.positions, fitById) : null;

  return (
    <WidgetContainer source="Portfolio positions · Fit">
      {posQuery.isError && (
        <div className="text-[13px]">
          <p className="text-muted-foreground">Could not load sector treemap.</p>
          <p className="mt-1 text-[11px] text-muted-foreground/70">
            {posQuery.error instanceof Error ? posQuery.error.message : String(posQuery.error)}
          </p>
        </div>
      )}

      {!posQuery.isError && loading && (
        <div className="space-y-3">
          <div className="h-5 w-32 animate-pulse rounded bg-accent" />
          <div className="h-[280px] w-full animate-pulse rounded bg-accent" />
        </div>
      )}

      {!posQuery.isError && !loading && sectors &&
        (() => {
          const totalChf = sectors.reduce((s, r) => s + r.chf, 0);
          let y = 0;
          const cells = sectors.map((sector) => {
            const h = totalChf > 0 ? (sector.chf / totalChf) * SVG_H : 0;
            const cell = { x: 0, y, w: SVG_W, h, sector };
            y += h;
            return cell;
          });
          return (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[15px] font-semibold text-foreground">Sector Treemap</span>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-destructive">● conflict</span>
                  <span className="text-amber-500">● partial</span>
                  <span className="text-emerald-600">● clean</span>
                </div>
              </div>

              {sectors.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">No holdings to show for this client yet.</p>
              ) : (
                (() => {
                  const totalChf = sectors.reduce((sum, sec) => sum + sec.chf, 0);
                  return (
                    <>
                      <svg width="100%" viewBox={`0 0 ${SVG_W} ${SVG_H}`} aria-label="Sector allocation treemap">
                        {cells.map(({ x, y: cy, w, h, sector }) => (
                          <TreemapCell key={sector.name} x={x} y={cy} w={w} h={h} sector={sector} totalChf={totalChf} />
                        ))}
                      </svg>
                      {/* TASK-654: text equivalent — every sector's %, value, and
                          holding count stays readable regardless of cell size. */}
                      <ul className="mt-2 grid grid-cols-1 gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground sm:grid-cols-2" data-testid="sector-legend">
                        {sectors.map((sec) => (
                          <li key={sec.name} className="flex justify-between gap-2">
                            <span className="truncate text-foreground">{sec.name}</span>
                            <span className="shrink-0 tabular-nums">
                              {totalChf > 0 ? ((sec.chf / totalChf) * 100).toFixed(1) : '0.0'}% ·{' '}
                              {sec.chf >= 1_000_000 ? `${(sec.chf / 1_000_000).toFixed(1)}M` : `${(sec.chf / 1_000).toFixed(0)}K`} ·{' '}
                              {sec.count} pos.
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  );
                })()
              )}

              <p className="mt-2 border-t border-border pt-3 text-[11px] text-muted-foreground/70">
                Source: Portfolio positions · CIO tags
              </p>
            </div>
          );
        })()}
    </WidgetContainer>
  );
}
